import React from 'react';
import Scroll from 'react-scroll';
import { useTranslate } from '../../mixins/translate/translate';
import { openApiFetchGet } from '../../store/openApiFetch';
import { Roles } from '../../utils/enums/Roles';
import {
    Content,
    LeftTray,
    LeftTrayItem,
    LeftTrayList,
    PanelHeading,
    PanelRouterLink,
    PanelWithoutForm,
    ScrollMenu,
    ScrollMenuItem,
    Topbar,
    TopbarCrumbActive,
    TopbarLeft,
    TopbarRight,
    Tray,
} from '../elements';
import { FlightLogs } from '../FlightLog/FlightLog';
import { useUserHasRole } from '../utils/authorization/useUserHasRole';
import { EquipmentExpirationLabel } from '../utils/equipmentExpirationLabel';
import { useReactAsync } from '../utils/useReactAsync';
import { EquipmentCarrierList } from './Common/EquipmentCarrierList';
import { EquipmentDocumentList } from './Common/EquipmentDocumentList';
import { EquipmentHistory } from './Common/EquipmentHistory';
import { EquipmentPayloadList } from './Common/EquipmentPayloadList';

async function loadEquipment({ equipmentId }: { equipmentId: string }) {
    const result = await openApiFetchGet('/api/equipment/{equipmentId}', { equipmentId });
    return result;
}

interface IFieldValueItem {
    id: string;
    equipmentTypeFieldId: string;
    name: string;
    fieldOrder: number;
    value: string | null;
}

async function loadFieldValues({ equipmentId }: { equipmentId: string }) {
    const result = await openApiFetchGet('/api/equipment/{equipmentId}/fieldValues', { equipmentId });
    return result as { items: IFieldValueItem[] } | undefined;
}

export function EquipmentDetails(props: { params: { id?: string } }) {
    if (!props.params.id) {
        throw new Error('Equipment id is not provided');
    }
    const translate = useTranslate();

    const equipmentId = props.params.id;
    if (!equipmentId) {
        throw new Error('equipmentId is required');
    }

    const { userHasRole } = useUserHasRole();
    const isAdmin = userHasRole([Roles.Administrator]);
    const { data } = useReactAsync(loadEquipment, { equipmentId: props.params.id }, [props.params.id]);
    const { data: fieldValuesData } = useReactAsync(loadFieldValues, { equipmentId: props.params.id }, [props.params.id]);
    const { scroller } = Scroll;

    function scrollTo(name: string) {
        scroller.scrollTo(name, {
            duration: 1500,
            delay: 100,
            smooth: true,
        });
    }

    return (
        <>
            <Topbar>
                <TopbarLeft>
                    <TopbarCrumbActive title={translate('equipment.equipment')} icon="fa-plane" />
                </TopbarLeft>
                <TopbarRight>
                    <PanelRouterLink
                        to={`/equipment/${props.params.id}/edit`}
                        text={translate('common.editItem', translate('equipment.equipment'))}
                        icon="fa fa-edit"
                        small={true}
                    />
                </TopbarRight>
            </Topbar>

            <Content>
                <LeftTray>
                    {data && (
                        <>
                            <LeftTrayList>
                                <LeftTrayItem title={translate('common.name')}>{data.name}</LeftTrayItem>
                                <LeftTrayItem title={translate('common.type')}>{data.equipmentType.name}</LeftTrayItem>
                                {isAdmin && <LeftTrayItem title={translate('common.client')}>{data.clientCompany.name}</LeftTrayItem>}
                                <LeftTrayItem title={translate('equipment.manufacturer')}>{data.manufacturer}</LeftTrayItem>
                                <LeftTrayItem title={translate('equipment.typeIndication')}>{data.typeIndication}</LeftTrayItem>
                                <LeftTrayItem title={translate('equipment.serialNumber')}>{data.serialNumber}</LeftTrayItem>
                                <LeftTrayItem title={translate('equipment.softwareVersion')}>{data.softwareVersion}</LeftTrayItem>
                                <LeftTrayItem title={translate('common.status')}>{translate(`equipment.${data.status}`)}</LeftTrayItem>
                                <LeftTrayItem title={translate('equipment.expirationDate')}>
                                    <EquipmentExpirationLabel data={data} />
                                </LeftTrayItem>
                                {fieldValuesData?.items &&
                                    fieldValuesData.items.map((fv) => (
                                        <LeftTrayItem key={fv.id} title={fv.name}>
                                            {fv.value}
                                        </LeftTrayItem>
                                    ))}
                            </LeftTrayList>
                            <ScrollMenu>
                                {data && (
                                    <ScrollMenuItem
                                        text={data.equipmentType.isPayload ? translate('equipment.carrier') : translate('equipment.payload')}
                                        icon="fa fa-suitcase"
                                        onClick={() => scrollTo('payload')}
                                    />
                                )}
                                <ScrollMenuItem text={translate('survey.documents')} icon="fa fa-book" onClick={() => scrollTo('documents')} />
                                <ScrollMenuItem text={translate('common.history')} icon="fa fa-clock-o" onClick={() => scrollTo('history')} />
                            </ScrollMenu>
                        </>
                    )}
                </LeftTray>
                <Tray>
                    {data && (
                        <PanelWithoutForm id="payload">
                            {(data.equipmentType.isPayload && (
                                <>
                                    <PanelHeading text={translate('equipment.carrier')} icon="fa fa-suitcase" />
                                    <EquipmentCarrierList equipmentId={equipmentId} />
                                </>
                            )) || (
                                <>
                                    <PanelHeading text={translate('equipment.payload')} icon="fa fa-suitcase" />
                                    <EquipmentPayloadList equipmentId={equipmentId} />
                                </>
                            )}
                        </PanelWithoutForm>
                    )}

                    <PanelWithoutForm id="documents">
                        <EquipmentDocumentList equipmentId={equipmentId} />
                    </PanelWithoutForm>

                    <PanelWithoutForm id="history">
                        <EquipmentHistory equipmentId={equipmentId} />
                    </PanelWithoutForm>

                    {data && data.equipmentType.hasLog && (
                        <PanelWithoutForm id="flightLog">
                            <FlightLogs equipmentId={equipmentId} />
                        </PanelWithoutForm>
                    )}
                </Tray>
            </Content>
        </>
    );
}
